import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  CREATE_APPLICANT,
  RECRUITMENT_STAGES,
} from '../data/recruitmentApi';
import '../styles/AddApplicant.css';

const INITIAL = {
  name: '',
  email: '',
  phone: '',
  role: '',
  experience: '',
  priority: 'normal',
  stage: 'new',
  branch: '',
  recruiter: '',
  notes: '',
};

export default function AddApplicant({ onClose, onCreated }) {
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Please enter applicant name');
      return;
    }
    if (!form.role.trim()) {
      toast.error('Please enter job position');
      return;
    }

    setSaving(true);
    try {
      const data = await graphqlRequest(CREATE_APPLICANT, {
        input: {
          name: form.name.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          role: form.role.trim(),
          experience: form.experience.trim() || null,
          priority: form.priority,
          stage: form.stage,
          branch: form.branch.trim() || null,
          recruiter: form.recruiter.trim() || null,
          notes: form.notes.trim() || null,
        },
      });
      const created = data?.createRecruitmentApplicant;
      if (!created?.id) throw new Error('Applicant was not created.');
      toast.success('Applicant added successfully.');
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Could not add applicant.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aa-overlay" onClick={onClose}>
      <div
        className="aa-modal"
        role="dialog"
        aria-labelledby="aa-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aa-header">
          <div>
            <h2 id="aa-title">Add Applicant</h2>
            <p>Create a new candidate in the recruitment pipeline.</p>
          </div>
          <button
            type="button"
            className="aa-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        <form className="aa-form" onSubmit={handleSubmit}>
          <div className="aa-grid">
            <label className="aa-field">
              <span>Full name *</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g. Daniel Kim"
                required
              />
            </label>

            <label className="aa-field">
              <span>Job position *</span>
              <input
                type="text"
                value={form.role}
                onChange={(e) => setField('role', e.target.value)}
                placeholder="e.g. Commercial Cleaner"
                required
              />
            </label>

            <label className="aa-field">
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="name@email.com"
              />
            </label>

            <label className="aa-field">
              <span>Phone</span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                placeholder="04xx xxx xxx"
              />
            </label>

            <label className="aa-field">
              <span>Experience</span>
              <input
                type="text"
                value={form.experience}
                onChange={(e) => setField('experience', e.target.value)}
                placeholder="e.g. 3 yrs"
              />
            </label>

            <label className="aa-field">
              <span>Branch</span>
              <input
                type="text"
                value={form.branch}
                onChange={(e) => setField('branch', e.target.value)}
                placeholder="e.g. CBD Melbourne"
              />
            </label>

            <label className="aa-field">
              <span>Recruiter</span>
              <input
                type="text"
                value={form.recruiter}
                onChange={(e) => setField('recruiter', e.target.value)}
                placeholder="e.g. Emily Watson"
              />
            </label>

            <label className="aa-field">
              <span>Priority</span>
              <select
                value={form.priority}
                onChange={(e) => setField('priority', e.target.value)}
              >
                <option value="high">High Priority</option>
                <option value="normal">Normal</option>
                <option value="low">Low Priority</option>
              </select>
            </label>

            <label className="aa-field">
              <span>Stage</span>
              <select
                value={form.stage}
                onChange={(e) => setField('stage', e.target.value)}
              >
                {RECRUITMENT_STAGES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="aa-field aa-field--full">
            <span>Notes</span>
            <textarea
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Optional notes about the applicant..."
              rows={3}
            />
          </label>

          <div className="aa-actions">
            <button
              type="button"
              className="aa-btn aa-btn--ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="aa-btn aa-btn--primary"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Applicant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
